import _ from 'lodash'
import { LOOKUP_TYPES } from '../components/dataTable/searchField'
import { getValuesFromArrayObjects } from './common'

/*
 * Determine if all userIds values are in the source using the supplied sourceKey
 * @params userIds Array userIds. example: [11,12,332]
 * @params source Array<Object> map of users. example: [{user_id: 11}, {user_id:12}]
 * @params sourceKey used to index into the objects in source
 * @returns Boolean
 */
const areAllUserIdsInSource = (source, userIds, sourceKey = 'user_id') => {
  return _.every(userIds, id => _.find(source, { [sourceKey]: id }))
}

/*
 * get the user ids from the userData with the given keys.
 * This will omit non numbers
 * @params userData Array<Object> userData source
 * example: [ {id: 2, 'created_by': 1222 }]
 * @params keys Array the user keys to look for in the userData objects
 * example: ['created_by', 'updated_by']
 * @returns Array
 *
 */
const getUserIdsFromSourceWithKeys = (source, keys) => {
  const unsanitizedUserIds = getValuesFromArrayObjects(source, keys)
  return _.filter(unsanitizedUserIds, userId => _.isNumber(userId))
}

/*
 * return all missing user ids in the source
 * @params source Array<Object> map of users. example: [{user_id: 11}, {user_id:12}]
 * @params values Array ids. example: [11,12,332]
 * @params sourceKey used to index into the objects in source
 *
 * @returns Array
 */
const getMissingUserIdsFromSource = (
  source,
  userIds,
  sourceKey = 'user_id'
) => {
  const unsanitizedUserIds = _.difference(userIds, _.map(source, sourceKey))
  return _.filter(unsanitizedUserIds, userId => _.isNumber(userId))
}

/**
 * Replace the specified keys with user.last_name, user.first_name
 * For example, replace id: 100 with doe, jane
 *
 * @params userData Array This should be the apiresult from searching for users
 * @params rowData Array original array of objects that needs key values replaced
 * @params keys Array keys' values that will need to be replaced in rowData
 * @params notFoundValue String value for userIds that are falsey (undefined, empty)
 * example: 'unknown'
 * @returns Array<Object>
 */
const replaceUserIdWithDisplayName = (
  userData,
  rowData,
  keys,
  notFoundValue = ''
) => {
  if (_.isEmpty(userData)) return rowData

  const clonedRowData = _.cloneDeep(rowData)
  const mappedUsers = {}
  // create a map of user_id to the display to easily pull values
  _.each(userData, user => {
    mappedUsers[user.user_id] = getFullName(user)
  })

  // replace keys in the objects with the display
  // if not found then just stringify the original value
  // if no value then replace with the notfoundvalue
  _.each(clonedRowData, row => {
    _.each(keys, key => {
      const rowValue = row[key]
      if (mappedUsers[rowValue]) {
        row[key] = String(mappedUsers[rowValue]).trim()
      } else if (!_.isNil(rowValue) && rowValue !== '') {
        row[key] = String(rowValue).trim()
      } else {
        row[key] = notFoundValue
      }
    })
  })

  return clonedRowData
}

/**
 * Replace the specified keys with user.last_name, user.first_name
 * For example, replace id: 100 with doe, jane
 * @param {*} mappedUsers { 12345: 'doe, jane', 12346: 'doe, john' }
 * @param {*} rowData Array original array of objects that needs key values replaced
 * @param {*} keys Array keys' values that will need to be replaced in rowData
 */
const replaceUserIdWithMappedUsersData = (mappedUsers, rowData, keys) => {
  mappedUsers['0'] = 'SYSTEM'

  return rowData.map(oldRow => {
    const row = _.cloneDeep(oldRow)
    _.each(keys, key => {
      const rowValue = _.get(row, key)
      _.set(row, key, mappedUsers[rowValue] ? mappedUsers[rowValue] : rowValue)
    })
    return row
  })
}

/**
 * Builds a UI-like name for a given user
 * @param {} user
 */
const getFullName = user => {
  let fullName = ''
  if (user.last_name) {
    fullName = user.last_name
  }

  if (user.first_name) {
    fullName = fullName ? `${fullName}, ${user.first_name}` : user.first_name
  }

  return fullName
}

/**
 * This method returns an object with user names indexed by user_id. If
 * no user name is present, then user_id is passed instead
 * @param {*} usersList array with user data. UAA format [{user_id: 123, first_name: 'name' ...}]
 * @param {*} userIdKeyField usually is user_id but it could be party_id
 */
const DEFAULT_USER_LOOKUP_FOREIGN_KEY_NAME = 'user_id'
const getMappedUsers = (
  usersList,
  userIdKeyField = DEFAULT_USER_LOOKUP_FOREIGN_KEY_NAME
) => {
  const mappedUsers = {}
  if (!_.isArray(usersList)) return mappedUsers
  _.each(usersList, user => {
    const fullName = getFullName(user)
    mappedUsers[user[userIdKeyField]] = fullName || user[userIdKeyField]
  })
  return mappedUsers
}

/**
 * This Method returns a sorted array with users with name first, then users without name
 * @param {*} userIds list of the users required
 * @param {*} usersDataMap map with users indexed by id { 123: 'Dowd, John', 201: 201 }
 */
const getSortedUsersList = (userIds, mappedUsers) => {
  // split list by users with/without name to send ninjas to the bottom
  const usersWithoutName = _.sortBy(
    userIds
      .filter(userId => !mappedUsers[userId] || mappedUsers[userId] === userId)
      .map(userId => ({
        text: `${userId}`,
        value: userId,
      })),
    ['text']
  )

  const usersWithName = _.sortBy(
    userIds
      .filter(userId => mappedUsers[userId] && mappedUsers[userId] !== userId)
      .map(userId => ({
        text: `${mappedUsers[userId]}`,
        value: userId,
      })),
    ['text']
  )
  return [...usersWithName, ...usersWithoutName]
}

/**
 *
 * @param {*} state redux state
 * @param {*} tableType tableType to lookup for information
 * This helper extract the list of columns which has a foreign key of user type inside table config
 */
const getUserForeignKeys = (state, tableType) => {
  const fieldDefinitions = _.get(
    state,
    `${tableType}.tableInfo.fieldDefinitions`,
    []
  )
  return fieldDefinitions
    .filter(field => field.foreignKey === LOOKUP_TYPES.USER)
    .map(field => field.apiRef)
}

export {
  areAllUserIdsInSource,
  getMissingUserIdsFromSource,
  getUserIdsFromSourceWithKeys,
  replaceUserIdWithDisplayName,
  replaceUserIdWithMappedUsersData,
  getMappedUsers,
  getSortedUsersList,
  getFullName,
  getUserForeignKeys,
}
